import { Injector } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MetadataService } from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { OnboardingIndividualErrorCodes, ProfileErrorCodes } from '@app/translations';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  KycStatus,
  OnboardUserResponse,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Cookies from 'js-cookie';
import { KeycloakService } from 'keycloak-angular';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ResetFormDataAction, SetFormActionWithId } from '../../../actions/form-submission.action';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytic.service';
import {
  OnboardIndividualRequest,
  PreviousAddressesEntry,
} from '../../../services/metadata-service/metadata-service.type';
import { PasswordService } from '../../../services/password-service/password-service.service';
import { FormActionState } from '../../../state/form-submision.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { setFormCompletionStatus } from '../../form-widget-action-utils/form-tracking.utils';
import { flattenObject } from '../../utils';
import { buildActionNavigationTargetUrl, handleRedirection } from '../cta-redirection.utils';
import { validateOnboardingOtpExpiry } from './onboarding-verification.utils';

/**
 * Where a newly onboarded customer comes back to after signing in at Keycloak.
 *
 * There is no `/login` route in this app — `''` and `**` both render
 * LoginComponent, which bounces straight to the identity provider — so "send them
 * to login" is always `keycloakService.login({ redirectUri: origin + this })`.
 * Exported so the custom-widget auth bridge lands new customers exactly where the
 * onboarding CTA does instead of inventing a destination.
 */
export const ONBOARDING_LOGIN_REDIRECT_PATH = '/feature-redirect';

export function onboardIndividual(injector: Injector, widgetProperties: BaseWidgetProperties): void {
  const router = injector.get(Router);
  const activeRoute = injector.get(ActivatedRoute);
  const store = injector.get(Store);
  const metaDataService = injector.get(MetadataService);
  const workflowFormService = injector.get(WorkflowFormsService);
  const keycloakService = injector.get(KeycloakService);
  const isValid = workflowFormService.currentPageFormsValid();
  const currentPageValidation = workflowFormService.validateCurrentPageForms();
  const currentPageForms = workflowFormService.getCurrentPageForms();
  const formValues = readCurrentPageFormsValues(currentPageForms, workflowFormService);
  // flattenObject loses array structure, so extract addressList before flattening
  const addressList = findAddressListInFormValues(formValues);
  const flattenedFormData = flattenObject(formValues); //flatten the form values as all pages are form groups
  if (addressList) {
    flattenedFormData['addressList'] = addressList;
  }
  store.dispatch(
    new SetFormActionWithId(
      {
        type: '',
        formData: flattenedFormData,
      },
      FormID.ONBOARDING_FLOW,
    ),
  );

  const currentZoneFromRoute = activeRoute?.snapshot?.params;
  if (currentPageValidation.invalidForms?.length === 0 && isValid) {
    validateOtpAndCopExpiryAndSubmit(currentZoneFromRoute, router, metaDataService, store, keycloakService, {
      widgetProperties,
      injector,
    });
  } else {
    workflowFormService.markCurrentPageFormsTouched();
  }
}

function validateOtpAndCopExpiryAndSubmit(
  currentZoneFromRoute: Params,
  router: Router,
  metaDataService: MetadataService,
  store: Store,
  keycloakService: KeycloakService,
  options: {
    widgetProperties: BaseWidgetProperties;
    injector: Injector;
    isRetryAfterServerRejection?: boolean;
  },
) {
  const { widgetProperties, injector, isRetryAfterServerRejection = false } = options;
  validateOnboardingOtpExpiry({
    formId: FormID.ONBOARDING_FLOW,
    widgetProperties,
    injector,
    includeCop: true,
    isRetryAfterServerRejection,
    submit: () =>
      submitOnboardUser(
        currentZoneFromRoute?.['role'],
        currentZoneFromRoute?.['zone'],
        router,
        metaDataService,
        store,
        keycloakService,
        widgetProperties,
        injector,
      ),
  });
}

async function submitOnboardUser(
  role: string,
  currentZone: string,
  router: Router,
  metaDataService: MetadataService,
  store: Store,
  keycloakService: KeycloakService,
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
) {
  const passwordService = injector.get(PasswordService);
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const alertHandlerService = injector.get(AlertHandlerService);
  const ctaAction = widgetProperties.buttonActionType as string;
  const onboardingFormData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormID.ONBOARDING_FLOW),
  )?.formData;

  const isLoggedOutZone =
    currentZone === APP_ZONES.ONBOARDING ||
    currentZone === APP_ZONES.ORGANISATION_ONBOARDING ||
    currentZone === APP_ZONES.ORGANISATION_VERIFICATION;

  // User registration is allowed only after both contact channels are verified
  // to ensure account security and prevent fraudulent sign-ups.
  if (!onboardingFormData?.['isPhoneVerified'] || !onboardingFormData?.['isEmailVerified']) {
    return findAndShowAlertFromCode(store, router, alertHandlerService, [
      ProfileErrorCodes.PROFILE_OTP_VERIFICATION_REQUIRED,
    ]);
  }

  if (Object.keys(onboardingFormData).length) {
    const passwordObjValue = passwordService.consumeAll();

    const onboardingFormRequestPayload = { ...onboardingFormData, ...passwordObjValue };

    //disable button on press
    ctaButtonSignalService.setSignal({ [ctaAction]: false });
    const email = onboardingFormRequestPayload?.email;
    //call onboard User API
    metaDataService
      .onboardIndividual(
        transformUserFormDataIntoRequestPayload(onboardingFormRequestPayload, role, injector),
      )
      .pipe(
        switchMap((response) =>
          setFormCompletionStatus(FormID.ONBOARDING_FLOW, injector).pipe(map(() => response)),
        ),
        tap((response) => {
          if (email) {
            Cookies.set('KEYCLOAK_REMEMBER_ME', `"username:${email}"`);
          }

          const status: Pick<OnboardUserResponse, 'status'> = { status: response.data.status };
          const outcomeType = getActionButtonOutcomeType(status);
          if (response?.data?.accessToken && response?.data?.refreshToken) {
            performAutoLogin(
              {
                accessToken: response?.data?.accessToken,
                refreshToken: response?.data?.refreshToken,
              },
              widgetProperties,
              injector,
              outcomeType,
              isLoggedOutZone,
            );
          } else if (widgetProperties?.['actionNavigationConfigurations']) {
            handleRedirection(widgetProperties, injector, outcomeType, true, isLoggedOutZone);
          } else {
            keycloakService.login({
              redirectUri: window.location.origin + ONBOARDING_LOGIN_REDIRECT_PATH,
            });
          }
          ctaButtonSignalService.setSignal({ [ctaAction]: true });
          store.dispatch(new ResetFormDataAction());
        }),
        catchError((err) => {
          console.error('Onboarding failed:', err);

          const alertCodes = err?.error?.alertCodes;

          if (alertCodes === OnboardingIndividualErrorCodes.PRODUCT_UNAVAILABLE) {
            handleProductUnavailable(widgetProperties, injector, isLoggedOutZone, onboardingFormData);
          } else if (alertCodes === ProfileErrorCodes.PROFILE_OTP_VERIFICATION_REQUIRED) {
            const roleAndZone: Params = {
              role: role,
              zone: currentZone,
            };
            // re-validate the expired details and send the user to re-confirmation flow
            validateOtpAndCopExpiryAndSubmit(roleAndZone, router, metaDataService, store, keycloakService, {
              widgetProperties,
              injector,
              isRetryAfterServerRejection: true,
            });
          } else {
            if (widgetProperties?.['actionNavigationConfigurations']) {
              handleRedirection(
                widgetProperties,
                injector,
                ActionButtonOutcomesType.ALTERNATE_1,
                true,
                isLoggedOutZone,
              );
            }
          }

          ctaButtonSignalService.setSignal({ [ctaAction]: true });
          return of(null);
        }),
      )
      .subscribe();
  } else {
    console.error('Error: Form data is not available');
  }
}

function getActionButtonOutcomeType(
  accountStatus: Pick<OnboardUserResponse, 'status'>,
): ActionButtonOutcomesType {
  let outcomeType: ActionButtonOutcomesType;
  switch (accountStatus.status) {
    case KycStatus.SUCCESS:
      outcomeType = ActionButtonOutcomesType.SUCCESS;
      break;
    case KycStatus.FAILED:
      outcomeType = ActionButtonOutcomesType.FAILURE;
      break;
    case KycStatus.PENDING:
      outcomeType = ActionButtonOutcomesType.PROCESSING;
      break;
    default:
      outcomeType = ActionButtonOutcomesType.ALTERNATE_1;
  }
  return outcomeType;
}

function handleProductUnavailable(
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  isLoggedOutZone: boolean,
  _onboardingFormData: any,
) {
  if (widgetProperties?.['actionNavigationConfigurations']) {
    handleRedirection(
      widgetProperties,
      injector,
      ActionButtonOutcomesType.PRODUCT_UNAVAILABLE,
      true,
      isLoggedOutZone,
    );
  }
}

/**
 * Consumes the session an onboarding response carried: store the tokens where
 * the Keycloak wrapper reads them back (`KeycloakWrapperService.getBaseKeycloakConfig`
 * reads exactly these sessionStorage keys) and hard-reload into /post-auth so
 * the app's initialisers and auth checks run against them.
 *
 * Exported because it is the *only* correct way to auto-login in this app and
 * there is now a second caller: the custom-widget onboarding bridge, whose
 * `onSuccess: 'signIn'` must be the same code path as this CTA rather than a
 * second copy of it that drifts. Callers that have no navigation target pass no
 * `returnUrl`; `''` is preserved as `?returnUrl=&autoLogin=true`, which is what
 * the CTA has always sent when its outcome config resolves to an empty url.
 */
export function performAutoLoginRedirect(
  authTokens: { accessToken: string; refreshToken: string; idToken?: string },
  returnUrl?: string,
): void {
  sessionStorage.setItem('token', authTokens?.accessToken as string);
  sessionStorage.setItem('refreshToken', authTokens?.refreshToken as string);
  // Optional because only the headless-login flow has an id_token to hand over.
  // `getBaseKeycloakConfig` seeds keycloak-js with it and the logout fallback needs
  // it for id_token_hint, so store it whenever a caller has one.
  if (authTokens?.idToken) sessionStorage.setItem('idToken', authTokens.idToken);
  const query = returnUrl === undefined ? '' : '?returnUrl=' + returnUrl + '&autoLogin=true';
  // Perform a hard redirect to the root URL so the app reloads from scratch,
  // allowing APP_INITIALIZER and authentication checks to run again
  window.location.replace('/post-auth' + query);
}

function performAutoLogin(
  authTokens: { accessToken: string; refreshToken: string },
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  type: ActionButtonOutcomesType,
  isLoggedOutZone = false,
) {
  performAutoLoginRedirect(
    authTokens,
    widgetProperties?.actionNavigationConfigurations?.[type]
      ? buildActionNavigationTargetUrl(widgetProperties, injector, type, isLoggedOutZone)
      : undefined,
  );
}

export function transformUserFormDataIntoRequestPayload(
  formData: Record<string, string>,
  role?: string,
  injector?: Injector,
  includeVerifiedFlags = true,
  excludeKeys: string[] = [],
): OnboardIndividualRequest {
  const googleAnalyticService = injector?.get(GoogleAnalyticsService);
  const baseFields = [
    'firstName',
    'middleName',
    'lastName',
    'dob',
    'email',
    'phoneNumber',
    'password',
    'country',
    'address1',
    'address2',
    'address3',
    'address4',
    'city',
    'county',
    'postalCode',
  ];

  const excludeFields = [
    'confirm_password',
    'search_address',
    'type',
    'countryCode',
    'direction',
    'isEmailVerified',
    'isPhoneVerified',
    'verifiedEmail',
    'verifiedPhone',
    'verifiedCountryCode',
    'isModal',
    'formSessionId',
    'addressList',
    'bankDetailsVerificationTokens',
    'emailVerifiedAt',
    'phoneVerifiedAt',
    // Promoted to top-level payload fields below; never let them fall through into extraAttributes,
    // where they would be persisted onto the Keycloak user profile.
    'emailVerificationToken',
    'phoneVerificationToken',
    ...excludeKeys,
  ];

  // Build the main payload from base fields
  const baseFieldValues: Record<string, unknown> = {};
  for (const key of baseFields) {
    if (formData[key] !== undefined) {
      baseFieldValues[key] = formData[key];
    }
  }

  // Extract extra fields (excluding base and excluded fields)
  const extraAttributes: Record<string, string> = {};
  for (const [key, value] of Object.entries(formData)) {
    if (!baseFields.includes(key) && !excludeFields.includes(key) && !key.startsWith('utm_')) {
      extraAttributes[key] = value;
    }
  }

  const campaign = googleAnalyticService?.getCampaignData();
  const googleAnalyticClientId = googleAnalyticService?.getFullGAClientId();

  // Add campaign into extraAttributes
  if (campaign) {
    Object.assign(extraAttributes, campaign);
  }
  // Add Google Analytic client ID
  if (googleAnalyticClientId) {
    extraAttributes['ga_client_id'] = googleAnalyticClientId;
  }

  if (includeVerifiedFlags) {
    // Handle emailVerified if present
    if (formData['isEmailVerified']) {
      baseFieldValues['emailVerified'] = formData['isEmailVerified'];
    }
    if (formData['isPhoneVerified']) {
      baseFieldValues['phoneVerified'] = formData['isPhoneVerified'];
    }
    // The verified* flags above are advisory client claims. These tokens are the actual proof the
    // server checks: it matches them against the ones it minted when the OTPs were verified.
    if (formData['emailVerificationToken']) {
      baseFieldValues['emailVerificationToken'] = formData['emailVerificationToken'];
    }
    if (formData['phoneVerificationToken']) {
      baseFieldValues['phoneVerificationToken'] = formData['phoneVerificationToken'];
    }
  }

  // Handle phoneNumber explicitly: concatenate countryCode + phoneNumber
  if (formData?.['countryCode']) {
    baseFieldValues['phoneNumber'] = `${formData?.['countryCode']}${formData?.['phoneNumber']}`;
  } else {
    baseFieldValues['phoneNumber'] = formData?.['phoneNumber'];
  }

  // Add mandatory role_name
  if (role) extraAttributes['role_name'] = role;

  const payload = {
    ...baseFieldValues,
  } as OnboardIndividualRequest;

  // Build address history fields from the addressList widget data when present
  const addressHistory = buildAddressHistoryFields((formData as any)['addressList']);
  if (addressHistory.currentAddress) {
    // Current address comes from addressList[0] — overrides any flat address fields
    Object.assign(payload, addressHistory.currentAddress);
  }
  if (addressHistory.currentAddressFromDate) {
    payload.currentAddressFromDate = addressHistory.currentAddressFromDate;
  }
  if (addressHistory.previousAddresses?.length) {
    payload.previousAddresses = addressHistory.previousAddresses;
  }

  // Add extraAttributes to payload only when it contains at least one key
  if (Object.keys(extraAttributes)?.length > 0) {
    payload.extraAttributes = extraAttributes;
  }

  // Include bankDetailsVerificationTokens if present in formData to avoid losing this data when the user retries onboarding after a failed verification attempt
  if (
    Array.isArray(formData?.['bankDetailsVerificationTokens']) &&
    formData?.['bankDetailsVerificationTokens'].length > 0
  ) {
    payload.bankDetailsVerificationTokens = formData?.['bankDetailsVerificationTokens'];
  }

  const jointAccountInvitationId = sessionStorage.getItem('jointAccountInvitationId');
  if (jointAccountInvitationId) {
    payload.jointAccountInvitationId = jointAccountInvitationId;
  }

  return payload;
}

function readCurrentPageFormsValues(
  currentPageForms: string[],
  workflowFormsService: WorkflowFormsService,
): Record<string, any> {
  const formValues: Record<string, any> = {};
  currentPageForms.forEach((formId) => {
    const formValue = workflowFormsService.getFormValues(formId);
    if (formValue) {
      formValues[formId] = formValue;
    }
  });
  return formValues;
}

// Searches the top-level form values (keyed by formId) for an addressList array.
function findAddressListInFormValues(formValues: Record<string, string>) {
  for (const value of Object.values(formValues)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (Array.isArray(value['addressList'])) {
        return value['addressList'];
      }
    }
  }
  return null;
}

type AddressHistoryFields = {
  currentAddressFromDate?: string;
  currentAddress?: {
    country: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    city: string;
    county: string;
    postalCode: string;
  };
  previousAddresses?: PreviousAddressesEntry[];
};

function buildAddressHistoryFields(addressList: any[] | undefined): AddressHistoryFields {
  if (!Array.isArray(addressList) || addressList.length === 0) {
    return {};
  }

  const result: AddressHistoryFields = {};

  const currentEntry = addressList[0];
  const currentMoveIn = parseAddressDate(currentEntry?.moveInDate);
  if (currentMoveIn) {
    result.currentAddressFromDate = formatAddressDate(currentMoveIn);
  }

  const currentAddr = currentEntry?.address ?? {};
  result.currentAddress = {
    country: currentAddr.country ?? '',
    address1: currentAddr.address1 ?? '',
    address2: currentAddr.address2 ?? '',
    address3: currentAddr.address3 ?? '',
    address4: currentAddr.address4 ?? '',
    city: currentAddr.city ?? '',
    county: currentAddr.county ?? '',
    postalCode: currentAddr.postalCode ?? '',
  };

  if (addressList.length > 1) {
    result.previousAddresses = [];
    for (let i = 1; i < addressList.length; i++) {
      const entry = addressList[i];
      const prevEntry = addressList[i - 1];

      const fromDate = parseAddressDate(entry?.moveInDate);
      if (!fromDate) continue;

      let toDateStr = '';
      const prevMoveIn = parseAddressDate(prevEntry?.moveInDate);
      if (prevMoveIn) {
        const toDt = new Date(prevMoveIn);
        toDt.setDate(toDt.getDate() - 1);
        toDateStr = formatAddressDate(toDt);
      }

      const addr = entry?.address ?? {};
      result.previousAddresses.push({
        country: addr.country ?? '',
        address1: addr.address1 ?? '',
        address2: addr.address2 ?? '',
        address3: addr.address3 ?? '',
        address4: addr.address4 ?? '',
        city: addr.city ?? '',
        county: addr.county ?? '',
        postalCode: addr.postalCode ?? '',
        fromDate: formatAddressDate(fromDate),
        toDate: toDateStr,
      });
    }
  }

  return result;
}

function parseAddressDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  const parsed = new Date(value as string);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function formatAddressDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
